import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { useContext } from "react";
import { Tables } from "types/supabase";

export const getBorrowersQueryKey = () => ["borrowers"];

type Borrower = Tables<"users"> & {
  company: {
    id: string;
    name: string;
  };
  entity?: {
    id: string;
    name: string;
  };
  contacts: Array<{
    id: string;
    user: {
      id: string;
      first_name: string;
      last_name: string;
      is_active: boolean;
    };
  }>;
  role: Tables<"roles">;
};

const fetchBorrowers = async (borrowerCompanyId?: string): Promise<Borrower[]> => {
  const { users }: { users: Borrower[] } = await apiRequest("/supabase/borrowers/select");

  if (borrowerCompanyId) {
    return users.filter(user => user.company.id === borrowerCompanyId);
  }

  // Sorting the borrowers by name
  return users.sort((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

type UseBorrowersProps = {
  enabled?: boolean;
  borrowerCompanyId?: string | undefined;
};

export const useBorrowers = ({ enabled = true, borrowerCompanyId }: UseBorrowersProps = {}) => {
  const { showError } = useContext(AlertsContext);

  const {
    data: borrowers = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery<Borrower[], Error>({
    queryKey: getBorrowersQueryKey(),
    queryFn: () => fetchBorrowers(borrowerCompanyId),
    gcTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 60 * 1000, // 1 minute
    enabled,
  });

  // Handle errors separately since React Query v5 doesn't have onError in options
  if (borrowers === null) {
    showError({
      title: "Borrowers Not Loaded",
      description: "Failed to load borrowers",
    });
  }

  return {
    loading: isLoading || isFetching,
    borrowers,
    fetchBorrowers: refetch,
  };
};
